import { styled } from 'baseui'
import { FlexBetween, FlexColumn } from '@/shared/theme/styled-components'
import { RAIL_ICON_HOVER_SCALE, RAIL_SLOT_SIZE } from '@/shared/theme/railSlot'

export const ToggleButton = styled('button', ({ $theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '2rem',
  height: '2rem',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  border: 0,
  borderRadius: '50%',
  backgroundColor: 'transparent',
  color: $theme.colors.mono500,
  cursor: 'pointer',
  ':hover': { color: $theme.colors.primary, backgroundColor: $theme.colors.bg050 },
  ':focus-visible': { outline: `2px solid ${$theme.colors.primary}`, outlineOffset: '2px' },
}))

// The vertical padding gives a folded module the height of the "schedule a callback" button.
export const StyledModuleHeader = styled<typeof FlexBetween, { $clickable: boolean }>(
  FlexBetween,
  ({ $clickable }) => ({
    width: '100%',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    paddingLeft: '0.9375rem',
    paddingRight: '0.9375rem',
    cursor: $clickable ? 'pointer' : 'default',
    userSelect: $clickable ? 'none' : 'auto',
  }),
)

export const ModuleTitleGroup = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.625rem',
  minWidth: 0,
})

export const ModuleIcon = styled('span', {
  position: 'relative',
  display: 'grid',
  placeItems: 'center',
  width: '1.875rem',
  height: '1.875rem',
  flexShrink: 0,
})

export const NotificationBadge = styled('span', ({ $theme }) => ({
  position: 'absolute',
  top: '-0.375rem',
  right: '-0.375rem',
  minWidth: '1.125rem',
  height: '1.125rem',
  paddingLeft: '0.25rem',
  paddingRight: '0.25rem',
  borderRadius: '999px',
  backgroundColor: $theme.colors.negative500,
  color: $theme.colors.white,
  fontSize: '0.6875rem',
  fontWeight: 700,
  lineHeight: '1.125rem',
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
  boxShadow: `0 0 0 0.125rem ${$theme.colors.white}`,
}))

export const StatusDot = styled('span', ({ $theme }) => ({
  position: 'absolute',
  top: '-0.125rem',
  right: '-0.125rem',
  width: '0.625rem',
  height: '0.625rem',
  borderRadius: '50%',
  backgroundColor: $theme.colors.negative400,
  boxShadow: `0 0 0 0.125rem ${$theme.colors.white}`,
}))

export const RailHeader = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
})

export const RailButton = styled('button', ({ $theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: RAIL_SLOT_SIZE,
  height: RAIL_SLOT_SIZE,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  border: 0,
  borderRadius: '0.5rem',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'background-color 0.12s',
  ':hover': { backgroundColor: $theme.colors.bg050 },
  ':hover svg': { transform: `scale(${RAIL_ICON_HOVER_SCALE})` },
  ':focus-visible': { outline: `2px solid ${$theme.colors.primary}`, outlineOffset: '2px' },
}))

export const ModuleTitle = styled('h3', ({ $theme }) => ({
  marginTop: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginRight: 0,
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: $theme.colors.contentPrimary,
}))

export const ModuleIndicator = styled('span', ({ $theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  color: $theme.colors.contentPrimary,
}))

export const HeaderActions = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
})

// No display of its own, so the `hidden` attribute folds it.
export const ModuleContent = styled('div', {
  width: '100%',
})

export const ModuleBody = styled<typeof FlexColumn, { $gap?: string }>(FlexColumn, ({ $gap = '1rem' }) => ({
  width: '100%',
  boxSizing: 'border-box',
  gap: $gap,
  paddingTop: 0,
  paddingBottom: '1rem',
  paddingLeft: '1rem',
  paddingRight: '1rem',
}))
